import type { UIMessage } from './types/mastraTypes';
import { SAMPLE_CONVERSATION_DATA } from './sample-data';
import { ConciergeDevOptions } from './types/devOptions';
import { resolveBrandclubHostDomain } from '../utils/domain';

/** Survey metadata from the backend */
export interface ConciergeSurveyData {
  campaignId: string;
  brandName: string;
  brandId?: number;
  title: string;
  rewardAmount: number;
  description?: string;
  brandLogoUrl?: string;
  previewImageUrl?: string;
}

const getDefaultApiBaseUrl = () => resolveBrandclubHostDomain();

// Flag to enable hardcoded conversation for testing
// Set to true to use hardcoded conversation, false to use normal API
export const USE_HARDCODED_CONVERSATION = false;

type UIPart = { type?: string; text?: string; [key: string]: unknown };

interface LoadedUIMessage {
  id?: string;
  role: string;
  parts?: UIPart[];
  content?: { format?: number; parts?: UIPart[] };
  createdAt?: string;
  messageIndex?: number;
  feedback?: { type: 'like' | 'dislike'; timestamp: string };
  followUpSuggestions?: string[];
}

interface MessagesEndpointResponse {
  conversationId?: string;
  messages?: LoadedUIMessage[];
  status?: 'in_progress' | 'completed' | 'error';
  followUpQuestions?: string[];
  isSharedView?: boolean;
  sharedConversationId?: string;
  sharedAtMessageIndex?: number;
  sharedByUserName?: string;
  sourceConversationId?: string;
  sourceCheckpointIndex?: number;
  error?: string;
  redirect?: string;
}

interface UnauthorizedResponse {
  redirect?: string;
}

type LoadConversationResponse =
  | {
      conversationId?: string;
      messages: UIMessage[];
      error?: 'unauthorized';
      redirect?: string;
      followUpQuestions?: string[];
      isSharedView?: boolean;
      sharedAtMessageIndex?: number;
      sharedByUserName?: string;
      sourceConversationId?: string;
      sharedConversationId?: string;
      sourceCheckpointIndex?: number;
      status?: 'in_progress' | 'completed' | 'error';
    }
  | null;

export const loadConversation = async ({
  conversationId,
  sharedConversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
  viewToken,
}: {
  conversationId: string | null;
  sharedConversationId?: string | null;
  headersToInject?: Record<string, string>;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
  /** Signed brand-view token. When present, appended as ?viewToken= to bypass ownership check. */
  viewToken?: string | null;
}): Promise<LoadConversationResponse> => {
  // If flag is enabled, return hardcoded conversation (even if conversationId is null)
  if (USE_HARDCODED_CONVERSATION) {
    return SAMPLE_CONVERSATION_DATA as LoadConversationResponse;
  }

  // If flag is disabled and no conversationId, return null
  if (!USE_HARDCODED_CONVERSATION && !conversationId && !sharedConversationId) {
    return null;
  }

  try {
    const headers: Record<string, string> = {
      ...headersToInject,
      'Content-Type': 'application/json',
    };

    const base = sharedConversationId
      ? `${apiBaseUrl}/api-rewards/concierge/conversation/shared/${sharedConversationId}/ui-messages`
      : `${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/ui-messages`;
    const url = viewToken ? `${base}?viewToken=${encodeURIComponent(viewToken)}` : base;

    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      if (response.status === 403) {
        const data = (await response.json()) as UnauthorizedResponse;
        return { messages: [], error: 'unauthorized', redirect: data.redirect };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const messagesData = (await response.json()) as MessagesEndpointResponse;
    if (messagesData.error) {
      console.error('Error loading conversation:', messagesData.error);
      return null;
    }

    const messages = (messagesData.messages || []).map((message: LoadedUIMessage) => {
      const id = message.id || `msg-${message.messageIndex ?? Date.now()}`;
      const role = (message.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant';
      const rawParts = message.content?.parts ?? message.parts;
      const parts = rawParts?.length ? rawParts : [{ type: 'text', text: '' }];
      return {
        id,
        role,
        parts,
        ...(message.createdAt ? { createdAt: message.createdAt } : {}),
        ...(message.messageIndex != null ? { messageIndex: message.messageIndex } : {}),
        ...(message.feedback ? { feedback: message.feedback } : {}),
        ...(Array.isArray(message.followUpSuggestions)
          ? { followUpSuggestions: message.followUpSuggestions }
          : {}),
      } as unknown as UIMessage;
    });

    return {
      conversationId: messagesData.conversationId || conversationId || undefined,
      status: messagesData.status,
      messages,
      followUpQuestions: messagesData.followUpQuestions || [],
      isSharedView: messagesData.isSharedView,
      sharedConversationId: messagesData.sharedConversationId || sharedConversationId || undefined,
      sharedAtMessageIndex: messagesData.sharedAtMessageIndex,
      sharedByUserName: messagesData.sharedByUserName,
      sourceConversationId: messagesData.sourceConversationId,
      sourceCheckpointIndex: messagesData.sourceCheckpointIndex,
    };
  } catch (error) {
    console.error('Failed to load conversation:', error);
    return null;
  }
};

/**
 * Send feedback (like/dislike) for a message
 */
export const sendMessageFeedback = async ({
  conversationId,
  messageIndex,
  feedbackType,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  messageIndex: number;
  feedbackType: 'like' | 'dislike' | null;
  headersToInject?: Record<string, string>;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
}): Promise<{
  message: {
    messageIndex: number;
    role: string;
    content: unknown;
    metadata: { feedback?: { type: 'like' | 'dislike'; timestamp: string } | null } & Record<string, unknown>;
    timestamp: string;
  };
}> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/message/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      conversationId,
      messageIndex,
      feedbackType,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Enable or disable sharing for a conversation.
 * When enabled with a messageIndex, creates a checkpoint — non-owners will only see
 * messages up to that index.
 */
export const setConversationSharing = async ({
  conversationId,
  enabled,
  messageIndex,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  enabled: boolean;
  /** The message index at which to create the share checkpoint */
  messageIndex?: number;
  headersToInject?: Record<string, string>;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
}): Promise<{
  sourceConversationId: string;
  sharedConversationId: string;
  shareUrl: string;
  sharedAtMessageIndex?: number;
}> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/share`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ enabled, messageIndex }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Update a conversation's title (e.g. when user renames from sidebar).
 */
export const updateConversationTitle = async ({
  conversationId,
  title,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  title: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<void> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations/${conversationId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ title: title.trim() }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update conversation title: ${response.status}`);
  }
};

/** An attachment on a turn being replayed, in the shape /agent/stream accepts. */
export interface ReplayFilePart {
  type: 'file';
  mediaType: string;
  url: string;
  filename?: string;
}

/**
 * Delete the last user+assistant turn and return the user's original prompt
 * AND its attachments, so the caller can re-stream the whole turn via
 * /concierge/agent/stream. Returning the text alone re-sent messages with
 * their screenshots stripped off (PetArmor QA row 52).
 */
export const retryConversation = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ prompt: string; fileParts?: ReplayFilePart[] }> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/retry`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as any).error || `HTTP ${response.status}`);
  }

  return response.json();
};

/**
 * End an active live-agent takeover from the user's side ("Return to
 * Concierge" button). Closes the linked Intercom conversation and flips the
 * thread state back to AI control.
 */
export const endLiveAgent = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ ok: boolean; noop?: boolean; reason?: string }> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };

  const response = await fetch(
    `${apiBaseUrl}/api-rewards/concierge/support/conversation/${conversationId}/end-live-agent`,
    {
      method: 'POST',
      headers,
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as any).error || `HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * Stop the in-flight assistant response, roll back the latest turn,
 * and return the user's original prompt so the caller can restore input.
 */
export const stopConversation = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ prompt: string; fileParts?: ReplayFilePart[] }> => {
  const headers: Record<string, string> = {
    ...headersToInject,
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/stop`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as any).error || `HTTP ${response.status}`);
  }

  return response.json();
};

/**
 * Fetch the ConciergeDevOptions defaults from the backend (DynamoDB-backed).
 * Returns null on failure so callers can fall back to hardcoded defaults.
 */
export const fetchDevOptions = async ({
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
} = {}): Promise<ConciergeDevOptions | null> => {
  try {
    const headers: Record<string, string> = {
      ...headersToInject,
      'Content-Type': 'application/json',
    };

    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/devOptions`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      console.warn('Failed to fetch dev options from server, using local defaults');
      return null;
    }

    return (await response.json()) as ConciergeDevOptions;
  } catch (error) {
    console.warn('Error fetching dev options:', error);
    return null;
  }
};

// Product data access lives in its own object-oriented module (ProductApi).
// Re-exported here so the Concierge barrel keeps a stable surface.
export { ProductApi, productApi } from './productApi';

export const fetchFollowUpSuggestions = async (
  conversationId: string,
  {
    headersToInject,
    apiBaseUrl = getDefaultApiBaseUrl(),
    devOptions,
  }: {
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
    devOptions?: { promptLabel?: string };
  } = {},
): Promise<string[]> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}/follow-ups`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ devOptions }),
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.questions) ? data.questions : [];
  } catch {
    return [];
  }
};

// ─── Voice mode (Plan 1) ─────────────────────────────────────────────────────

/**
 * OpenAI Realtime function-tool schema as forwarded by the server's
 * `prepareVoiceCall` for inclusion in the WebRTC `session.update` payload.
 */
export interface VoiceFunctionTool {
  type: 'function';
  name: string;
  description: string;
  parameters: unknown;
}

export interface VoiceSessionConfig {
  type: 'realtime';
  model: string;
  output_modalities: string[];
  instructions: string;
  audio: {
    input?: {
      transcription?: {
        model: string;
        language?: string;
        delay?: 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
      };
      turn_detection?: {
        type: string;
        interrupt_response?: boolean;
        create_response?: boolean;
        eagerness?: 'low' | 'medium' | 'high' | 'auto';
        threshold?: number;
        silence_duration_ms?: number;
        prefix_padding_ms?: number;
      };
    };
    output?: {
      voice: string;
    };
  };
  tools: VoiceFunctionTool[];
  tool_choice: 'auto';
}

export interface VoiceSessionMintResponse {
  conversationId: string;
  ephemeralKey: string;
  expiresAt: number;
  sessionConfig: VoiceSessionConfig;
  greeting: string;
  eventChannelUrl: string;
}

/** POST /concierge/voice/session — mint OpenAI ephemeral key + session config. */
export const mintVoiceSession = async ({
  conversationId,
  pdpStacklineSku,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  pdpStacklineSku?: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<VoiceSessionMintResponse> => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/session`, {
    method: 'POST',
    headers: { ...headersToInject, 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId, pdpStacklineSku }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`mintVoiceSession failed (${response.status}): ${body.slice(0, 200)}`);
  }
  return (await response.json()) as VoiceSessionMintResponse;
};

/**
 * Plan 7c / D-64 — a single prior-thread text turn projected for voice priming.
 * Replayed as a `conversation.item.create` `message` item over the WebRTC data
 * channel before the first `response.create`.
 */
export interface PriorThreadTextItem {
  kind: 'text';
  role: 'user' | 'assistant';
  text: string;
}

/**
 * D-69 — a single prior-thread tool round-trip projected for voice priming.
 * Replayed as a `function_call` + `function_call_output` pair so the resumed
 * model has SKU/order/etc. continuity from the prior leg.
 *
 * Sourced from either:
 *  - voice persistence (streaming format): a `tool-call` part paired with a
 *    matching `tool-result` part by `toolCallId`. Output is the persisted
 *    narrator (with INTERNAL SKU block after D-69 BE fix).
 *  - text persistence (Mastra v2 recalled format): a `tool-invocation` part
 *    with `state: 'result'`. Output is the JSON-stringified `result` field,
 *    capped to keep the priming payload bounded.
 */
export interface PriorThreadToolItem {
  kind: 'tool';
  /** Original `toolCallId` from the prior call, used to link the pair. */
  callId: string;
  toolName: string;
  /** Already JSON-stringified args (OpenAI Realtime expects a string here). */
  argsJson: string;
  /**
   * The function's output string. For voice rows, the persisted narrator with
   * the `[INTERNAL — do not speak aloud]` block (per D-27). For text rows,
   * the JSON-stringified tool result capped at `MAX_TOOL_OUTPUT_CHARS`.
   * System instructions already teach the model to never read INTERNAL blocks
   * aloud; that contract applies equally to replayed history.
   */
  output: string;
}

export type PriorThreadItem = PriorThreadTextItem | PriorThreadToolItem;

/**
 * @deprecated D-69 — use `PriorThreadItem` (text + tool union). Kept as a thin
 * alias for back-compat with any external consumers that imported the original
 * text-only shape from Plan 7c.
 */
export type PriorThreadMessage = PriorThreadTextItem;

/** Plan 7c / D-64 default cap on text messages replayed into the session. */
const DEFAULT_PRIMING_TEXT_LIMIT = 20;
/** D-69 default cap on tool round-trips replayed (most recent N within the text window). */
const DEFAULT_PRIMING_TOOL_LIMIT = 3;
/**
 * D-69 — per-tool-output cap (chars) to keep priming bounded when replaying
 * text-mode `tool-invocation` results, which can carry full structured tool
 * output (e.g. `search_products` returning 30 product objects). Voice
 * narrators are already capped at 1500 chars upstream so this only bites the
 * text-mode case.
 */
const MAX_TOOL_OUTPUT_CHARS = 2000;

/**
 * Plan 7c / D-64 (text replay) + D-69 (tool replay) — fetch the prior thread's
 * text turns + recent tool round-trips so the voice hook can seed conversation
 * history into the resumed OpenAI Realtime session.
 *
 * Best-effort: on any failure (network blip, 403, 404, malformed payload)
 * returns `[]` so the voice call still connects and falls through to the
 * standard greeting. Never throws.
 *
 * Projection rules:
 * - Text items per D-64 §3 (corrected — see D-64 §4): join every `type === 'text'`
 *   part per message with `\n\n`; drop messages whose joined text is empty;
 *   coerce unknown roles to `assistant`.
 * - Tool items per D-69: pair `tool-call` parts with the matching `tool-result`
 *   part by `toolCallId`; output is the persisted narrator (with INTERNAL block
 *   if `narratorFull` was stored post-D-69). Orphans (call without result, or
 *   result without call in window) are skipped.
 * - Items returned in chronological order. Text items capped to `limit`
 *   (default 20). Tool items capped to `toolLimit` (default 3) — the OLDEST
 *   tool round-trips beyond the cap are dropped; text turns are preserved.
 *
 * Calls `/ui-messages?includeToolParts=true` so the backend keeps voice-shape
 * `tool-call` / `tool-result` parts in the response (D-69 BE change).
 */
export const loadPriorThreadMessages = async ({
  conversationId,
  limit = DEFAULT_PRIMING_TEXT_LIMIT,
  toolLimit = DEFAULT_PRIMING_TOOL_LIMIT,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  limit?: number;
  toolLimit?: number;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<PriorThreadItem[]> => {
  if (!conversationId) return [];

  try {
    const headers: Record<string, string> = {
      ...headersToInject,
      'Content-Type': 'application/json',
    };
    const url =
      `${apiBaseUrl}/api-rewards/concierge/conversation/${conversationId}` +
      `/ui-messages?includeToolParts=true`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) return [];

    let messagesData: MessagesEndpointResponse;
    try {
      messagesData = (await response.json()) as MessagesEndpointResponse;
    } catch {
      return [];
    }
    if (messagesData.error) return [];

    const allMessages = messagesData.messages ?? [];
    const safeLimit = Math.max(0, Math.floor(limit));
    // Window to the last `limit` messages chronologically; the tool cap is
    // applied within this window so older tool round-trips don't reappear via
    // the text window resize.
    const windowed = safeLimit > 0 ? allMessages.slice(-safeLimit) : allMessages;

    const items: PriorThreadItem[] = [];
    // Pending tool-calls keyed by toolCallId. We only commit the round-trip
    // when the matching tool-result lands in the same window (per D-69
    // "items-without-pair edge cases").
    const pendingToolCalls = new Map<string, { toolName: string; argsJson: string }>();

    for (const message of windowed) {
      const role: 'user' | 'assistant' = message.role === 'user' ? 'user' : 'assistant';
      const rawParts = message.content?.parts ?? message.parts ?? [];

      const text = rawParts
        .filter((p) => p && p.type === 'text' && typeof p.text === 'string')
        .map((p) => p.text as string)
        .join('\n\n');
      if (text) items.push({ kind: 'text', role, text });

      for (const part of rawParts) {
        if (!part?.type) continue;

        if (part.type === 'tool-call' && typeof (part as any).toolCallId === 'string') {
          // Streaming format (voice persistence via persistTranscriptsFromEvents).
          const callId = (part as any).toolCallId as string;
          const toolName = typeof (part as any).toolName === 'string' ? ((part as any).toolName as string) : '';
          let argsJson = '{}';
          try {
            argsJson = JSON.stringify((part as any).args ?? {});
          } catch {
            argsJson = '{}';
          }
          if (callId && toolName) {
            pendingToolCalls.set(callId, { toolName, argsJson });
          }
        } else if (part.type === 'tool-result' && typeof (part as any).toolCallId === 'string') {
          // Streaming format pair-completion (voice).
          const callId = (part as any).toolCallId as string;
          const pending = pendingToolCalls.get(callId);
          if (pending) {
            const narrator = (part as any)?.result?.narrator;
            items.push({
              kind: 'tool',
              callId,
              toolName: pending.toolName,
              argsJson: pending.argsJson,
              output: typeof narrator === 'string' ? narrator : '',
            });
            pendingToolCalls.delete(callId);
          }
        } else if (part.type === 'tool-invocation' && (part as any).toolInvocation) {
          // Mastra v2 recalled format (text-mode tool calls land here after
          // their streaming `tool-call`+`tool-result` parts get folded into a
          // single resolved tool-invocation by the agent SDK). We treat the
          // resolved invocation as a complete round-trip in one shot.
          const ti = (part as any).toolInvocation as {
            state?: string;
            toolCallId?: string;
            toolName?: string;
            args?: unknown;
            result?: unknown;
          };
          if (ti.state === 'result' && typeof ti.toolCallId === 'string' && typeof ti.toolName === 'string') {
            let argsJson = '{}';
            try {
              argsJson = JSON.stringify(ti.args ?? {});
            } catch {
              argsJson = '{}';
            }
            let output = '';
            try {
              output = JSON.stringify(ti.result ?? {});
              if (output.length > MAX_TOOL_OUTPUT_CHARS) {
                output = output.slice(0, MAX_TOOL_OUTPUT_CHARS) + '…[truncated]';
              }
            } catch {
              output = '';
            }
            items.push({
              kind: 'tool',
              callId: ti.toolCallId,
              toolName: ti.toolName,
              argsJson,
              output,
            });
          }
        }
      }
    }

    // Tool-cap: drop the OLDEST tool round-trips beyond `toolLimit`. Text items
    // are preserved regardless so the model retains the conversational arc.
    const safeToolLimit = Math.max(0, Math.floor(toolLimit));
    const toolCount = items.reduce((n, it) => (it.kind === 'tool' ? n + 1 : n), 0);
    const toolsToDrop = Math.max(0, toolCount - safeToolLimit);
    let capped = items;
    if (toolsToDrop > 0) {
      let droppedSoFar = 0;
      capped = items.filter((it) => {
        if (it.kind !== 'tool') return true;
        if (droppedSoFar < toolsToDrop) {
          droppedSoFar++;
          return false;
        }
        return true;
      });
    }
    return stripCallEndingNoise(capped);
  } catch {
    return [];
  }
};

/** Tool round-trips that only signal the prior call hung up — never useful as resumed history. */
const CALL_ENDING_TOOL_NAMES = new Set(['end_call']);

/**
 * Short trailing farewells ("bye", "goodbye", "talk later", "take care", …).
 * Length-guarded by the caller so a substantive final turn that merely contains
 * the word "bye" isn't dropped.
 */
const FAREWELL_RE = /\b(bye|goodbye|see ya|see you|talk (to you )?later|catch you later|take care|have a (good|great|nice) (day|one|evening))\b/i;

/**
 * Strip call-ending noise from replayed prior context so a resumed voice call
 * doesn't open by saying goodbye (observed live: a follow-up call greeted with
 * "Goodbye!" because the prior leg's `end_call` round-trip + farewell turns were
 * the most-recent context). Drops `end_call` round-trips anywhere, then peels
 * trailing short farewell text turns (user or assistant) off the end.
 */
const stripCallEndingNoise = (items: PriorThreadItem[]): PriorThreadItem[] => {
  const withoutEndCall = items.filter(
    (it) => !(it.kind === 'tool' && CALL_ENDING_TOOL_NAMES.has(it.toolName)),
  );
  let end = withoutEndCall.length;
  while (end > 0) {
    const last = withoutEndCall[end - 1];
    const isShortFarewell =
      last.kind === 'text' && last.text.trim().length <= 80 && FAREWELL_RE.test(last.text);
    if (!isShortFarewell) break;
    end -= 1;
  }
  return end === withoutEndCall.length ? withoutEndCall : withoutEndCall.slice(0, end);
};

/** POST /concierge/voice/event — fire-and-forget event ingest. */
export const postVoiceEvent = async ({
  conversationId,
  events,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  events: any[];
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<void> => {
  try {
    await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/event`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, events }),
      keepalive: true,
    });
  } catch {
    // Heartbeats and event posts are best-effort; swallow network failures.
  }
};

/** A single widget emit returned by `/voice/tool` (D-46). */
export interface VoiceToolWidgetEmit {
  type: string;
  data: unknown;
}

/** Response shape of `POST /concierge/voice/tool` (D-46 adds `widgets`). */
export interface ProxyVoiceToolResponse {
  status: 'ok' | 'error';
  narratorText: string;
  toolResultId: string;
  endCallAfterMs?: number;
  /**
   * Reason stamped on `/voice/end` when `endCallAfterMs` fires. Set by
   * tools that want their hangup path to surface separately in admin
   * replay + Mixpanel (e.g. `end_call` ⇒ `ended_by_model`). When omitted,
   * the hook falls back to `call_ended_by_tool` for backward compat with
   * `escalate_to_support`.
   */
  endCallReason?: string;
  /**
   * Widgets emitted by the tool during this call, in emit order. The FE
   * applies a "latest non-`clear` wins, `clear` resets to orb" reducer.
   * Replaces the SSE `data-widget-*` delivery path. Always present on
   * `status === 'ok'` (may be empty); omitted on error.
   */
  widgets?: VoiceToolWidgetEmit[];
}

/** POST /concierge/voice/tool — execute a tool the model invoked. */
export const proxyVoiceTool = async ({
  conversationId,
  callId,
  toolName,
  args,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  callId: string;
  toolName: string;
  args: string | Record<string, unknown>;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<ProxyVoiceToolResponse> => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/tool`, {
    method: 'POST',
    headers: { ...headersToInject, 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId, callId, toolName, args }),
  });
  if (!response.ok) {
    return {
      status: 'error',
      narratorText: 'Sorry, I had a problem with that. Want to try again?',
      toolResultId: `${callId}-fail-${Date.now()}`,
    };
  }
  return response.json();
};

export interface VoiceEndResponse {
  ok: true;
  conversationId: string;
  durationSec: number;
  reason: string;
  mastraThreadCreated: boolean;
  messagesPersisted: number;
  ticketIdsCreated: string[];
}

/** POST /concierge/voice/end — finalize the call + persist transcripts. */
export const endVoiceCall = async ({
  conversationId,
  reason,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  reason?: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<VoiceEndResponse | null> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/end`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, reason }),
      keepalive: true,
    });
    if (!response.ok) return null;
    return (await response.json()) as VoiceEndResponse;
  } catch {
    return null;
  }
};

/**
 * POST /concierge/voice/auth-promote — explicit signal that the on-screen
 * login widget just completed sign-in (D-48). Fires `voice_login_completed`
 * on Mixpanel + stamps a durable `login.completed` row, and synchronously
 * promotes `state.userId` so a parallel tool call landing milliseconds
 * later is guaranteed to see `isAuthenticated: true`.
 *
 * `headersToInject` MUST include the freshly-refreshed `Authorization`
 * bearer (post `inlineVerifyOtp`); the backend rejects with 401 otherwise.
 */
export const postVoiceAuthPromote = async ({
  conversationId,
  durationMs,
  blockedToolName,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  durationMs?: number;
  blockedToolName?: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ ok: boolean; wasVisitor?: boolean }> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/auth-promote`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, durationMs, blockedToolName }),
    });
    if (!response.ok) return { ok: false };
    return (await response.json()) as { ok: boolean; wasVisitor?: boolean };
  } catch {
    return { ok: false };
  }
};

/**
 * POST /concierge/voice/debug/widget — emit a test widget or status event
 * into a live call's event channel (dev-only endpoint; env-gated on the
 * server). Used by the widgets preview page and manual verification.
 */
export const postVoiceDebugWidget = async ({
  conversationId,
  type,
  data,
  status,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  /** Widget type (e.g. 'comparison', 'clear') OR undefined when sending a status ping. */
  type?: string;
  data?: unknown;
  /** When set, emits a `data-status` event instead of (or in addition to) a widget event. */
  status?: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ ok: boolean }> => {
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/debug/widget`, {
    method: 'POST',
    headers: { ...headersToInject, 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId, type, data, status }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`postVoiceDebugWidget failed (${response.status}): ${body.slice(0, 200)}`);
  }
  return response.json();
};

/**
 * Open the long-lived SSE event channel for `data-status` events emitted
 * server-side by the publisher (D-46: widgets now travel in the
 * `/voice/tool` HTTP response, so this channel only carries the cosmetic
 * mid-execution status preamble).
 *
 * Returns an `EventSource` the caller is responsible for `.close()`-ing
 * when the call ends. Note: native EventSource does not support custom
 * headers, so both the JWT (signed-in users) and the visitor id (logged-out
 * users) are forwarded as query params; the backend synthesizes the
 * equivalent `Authorization` / `x-bc-visitor-id` headers before running
 * `assertOwnership`.
 */
export const streamVoiceEventChannel = ({
  conversationId,
  accessToken,
  visitorId,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  /**
   * Bearer JWT for the signed-in user. The browser EventSource API does NOT
   * support custom headers (cannot set `Authorization`), so we pass it as a
   * `?token=` query param. The backend extracts it back into an
   * `Authorization: Bearer ...` header before running `assertOwnership`.
   */
  accessToken?: string;
  /**
   * Visitor id for logged-out users (D-46). Same constraint as `accessToken`
   * — `EventSource` cannot set the `x-bc-visitor-id` header, so the FE
   * forwards it via `?visitorId=` and the backend synthesizes the header
   * before `assertOwnership`. Without this, every visitor SSE handshake
   * 403'd because `state.visitorId` was set but the request had no visitor
   * id to match against.
   */
  visitorId?: string;
  apiBaseUrl?: string;
}): EventSource => {
  const base = `${apiBaseUrl}/api-rewards/concierge/voice/event-channel/${encodeURIComponent(conversationId)}`;
  const params = new URLSearchParams();
  if (accessToken) params.set('token', accessToken);
  if (visitorId) params.set('visitorId', visitorId);
  const qs = params.toString();
  const url = qs ? `${base}?${qs}` : base;
  return new EventSource(url, { withCredentials: true });
};

// ─── Voice recording (Plan 3b) ───────────────────────────────────────────────

export type VoiceRecordingTrack = 'user' | 'assistant';

export interface VoiceRecordingInitResponse {
  enabled: boolean;
  prefix?: string;
  keys?: { user: string; assistant: string };
  /** Presigned PutObject URLs minted up-front; FE PUTs full blobs to these on stop. */
  presignedUrls?: { user: string; assistant: string };
  /** Mime each presigned URL is bound to (FE PUT must send matching `Content-Type`). */
  mimeUser?: string;
  mimeAssistant?: string;
  reason?: string;
}

/** POST /concierge/voice/recording/init — mint presigned PutObject URLs for both tracks. */
export const initVoiceRecording = async ({
  conversationId,
  mimeUser,
  mimeAssistant,
  extUser,
  extAssistant,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  mimeUser: string;
  mimeAssistant: string;
  extUser: string;
  extAssistant: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<VoiceRecordingInitResponse> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/recording/init`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, mimeUser, mimeAssistant, extUser, extAssistant }),
    });
    if (!response.ok) return { enabled: false, reason: `http_${response.status}` };
    return (await response.json()) as VoiceRecordingInitResponse;
  } catch {
    return { enabled: false, reason: 'network_error' };
  }
};

/**
 * POST /concierge/voice/recording/finalize — record per-track metadata after
 * the FE has PUT the blob to its presigned URL. `uploadOk: false` tells the
 * server to mark the row's `recordingMissing` flag without poking S3.
 */
export const finalizeVoiceRecording = async ({
  conversationId,
  track,
  durationMs,
  mimeType,
  byteSize,
  uploadOk,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  track: VoiceRecordingTrack;
  durationMs: number;
  mimeType: string;
  byteSize: number;
  uploadOk: boolean;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<{ ok: boolean; bothTracksFinalized?: boolean; error?: string }> => {
  try {
    const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/voice/recording/finalize`, {
      method: 'POST',
      headers: { ...headersToInject, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, track, durationMs, mimeType, byteSize, uploadOk }),
      keepalive: true,
    });
    if (!response.ok) return { ok: false, error: `http_${response.status}` };
    return (await response.json()) as { ok: boolean; bothTracksFinalized?: boolean };
  } catch {
    return { ok: false, error: 'network_error' };
  }
};

// ─── Voice public summary (Plan 4c) ─────────────────────────────────────────

export interface VoicePublicSummary {
  conversationId: string;
  summaryTitle?: string;
  summaryText?: string;
  summaryHighlights?: { label: string; value: string }[];
  durationSec?: number;
  toolsCalled?: string[];
  endReason?: string;
}

/**
 * GET /concierge/voice/sessions/:id/public-summary
 *
 * Consumer-safe summary for the post-call Summary view (Plan 4c).
 * Returns `null` on 404 (session doesn't exist yet or call never started),
 * so callers can distinguish "no session" from "summary not ready yet"
 * (the latter: non-null response but `summaryText` absent).
 */
export const getVoicePublicSummary = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  headersToInject?: Record<string, string>;
  apiBaseUrl?: string;
}): Promise<VoicePublicSummary | null> => {
  try {
    const response = await fetch(
      `${apiBaseUrl}/api-rewards/concierge/voice/sessions/${encodeURIComponent(conversationId)}/public-summary`,
      { headers: { ...headersToInject }, credentials: 'include' },
    );
    if (response.status === 404) return null;
    if (!response.ok) return null;
    return (await response.json()) as VoicePublicSummary;
  } catch {
    return null;
  }
};

/**
 * Returns the URL for the consumer-facing transcript markdown download
 * (GET /concierge/voice/sessions/:id/transcript.md).
 *
 * The FE renders this as a plain `<a href={...} download>` anchor — no
 * client-side fetch needed; the server streams the file directly.
 */
export const voiceTranscriptMarkdownUrl = ({
  conversationId,
  apiBaseUrl = getDefaultApiBaseUrl(),
}: {
  conversationId: string;
  apiBaseUrl?: string;
}): string =>
  `${apiBaseUrl}/api-rewards/concierge/voice/sessions/${encodeURIComponent(conversationId)}/transcript.md`;
